import { faChevronCircleLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FC } from 'react';
import type { CapacityManagementRootResponse } from 'src/apis/capacity-management/types';
import type { NetworkPoint } from 'src/apis/monolith/types';
import { useOptionalOrganisationDetails } from 'src/apis/organisation-service/use-organisation-details';
import { useOptionalOrganisationService } from 'src/apis/organisation-service/use-organisation-service';
import { Direction } from 'src/components/data-display/direction';
import { MetaLabel } from 'src/components/data-display/meta-label';
import { PageHeader } from 'src/components/layout/page-header';
import { ExternalLink, Link } from 'src/components/navigation/link';
import { Tabs } from 'src/components/navigation/tabs';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';

type Props = {
  networkPoint: NetworkPoint;
  hasPublicCapacityInformation: boolean;
  capacityManagementRoot?: CapacityManagementRootResponse;
};

export const NetworkPointHeader: FC<Props> = ({
  networkPoint,
  hasPublicCapacityInformation,
  capacityManagementRoot,
}) => {
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  if (monolithUser?.tsoName) {
    return (
      <HeaderInternal
        networkPoint={networkPoint}
        capacityManagementRoot={capacityManagementRoot}
        isShipper={monolithUser.isShipper}
        isMyNetworkPoint={monolithUser.tsoName === networkPoint.tso0Name} // looking only at non-bundles for now
        hasPublicCapacityInformation={hasPublicCapacityInformation}
      />
    );
  }
  return (
    <HeaderFetchingOrganisationService
      networkPoint={networkPoint}
      capacityManagementRoot={capacityManagementRoot}
      isShipper={!!monolithUser?.isShipper}
      hasPublicCapacityInformation={hasPublicCapacityInformation}
    />
  );
};

const HeaderInternal: FC<
  Props & {
    isShipper: boolean;
    isMyNetworkPoint: boolean;
  }
> = ({
  networkPoint,
  capacityManagementRoot,
  isShipper,
  isMyNetworkPoint,
  hasPublicCapacityInformation,
}) => {
  return (
    <>
      <PageHeader
        title={networkPoint.name}
        metaLabels={
          <>
            <Direction networkPoint={networkPoint} />
            {networkPoint.active === false ? (
              <MetaLabel color="inactive">Inactive</MetaLabel>
            ) : null}
          </>
        }
        backLink={
          <Link to="/transport/network-points" mode="default">
            <FontAwesomeIcon icon={faChevronCircleLeft} aria-hidden /> Back to
            Network Points
          </Link>
        }
      />

      {(isMyNetworkPoint ||
        (isShipper && !networkPoint.bundled) ||
        hasPublicCapacityInformation) && (
        <Tabs>
          <ExternalLink
            href={`${PRISMA_CONFIG.angularUrl}/#/network-point/details/${networkPoint.id}`}
          >
            Point Details
          </ExternalLink>
          {/* swap link to "Point Details" with TO-1343 */}
          {/* <Link
            to={`/transport/network-points/${networkPoint.id}`}
            isActive={
              pathname === `/transport/network-points/${networkPoint.id}`
            }
          >
            Point Details
          </Link> */}

          {isMyNetworkPoint &&
            capacityManagementRoot?._links?.viewAvailabilityAndPriceData && (
              <Link
                to={`/transport/network-points/${networkPoint.id}/availability-and-price-data`}
              >
                Availability & Price Data
              </Link>
            )}

          {isMyNetworkPoint &&
            capacityManagementRoot?._links?.capacityGraph && (
              <Link
                to={`/transport/network-points/${networkPoint.id}/capacity-overview`}
              >
                Capacity Overview
              </Link>
            )}

          {isShipper && (
            <Link
              to={`/transport/network-points/${networkPoint.id}/my-portfolio-overview`}
            >
              Portfolio Overview
            </Link>
          )}

          {hasPublicCapacityInformation &&
            !capacityManagementRoot?._links?.capacityGraph && (
              <Link
                to={`/transport/network-points/${networkPoint.id}/capacity-overview-fcfs`}
              >
                Capacity Overview
              </Link>
            )}
        </Tabs>
      )}
    </>
  );
};

const HeaderFetchingOrganisationService: FC<Props & { isShipper: boolean }> = ({
  networkPoint,
  capacityManagementRoot,
  isShipper,
  hasPublicCapacityInformation,
}) => {
  const organisationService = useOptionalOrganisationService();
  const myOrgDetails = useOptionalOrganisationDetails({ organisationService });

  return (
    <HeaderInternal
      networkPoint={networkPoint}
      capacityManagementRoot={capacityManagementRoot}
      isShipper={isShipper}
      isMyNetworkPoint={
        myOrgDetails?.response?.data.companyName === networkPoint.tso0Name
      }
      hasPublicCapacityInformation={hasPublicCapacityInformation}
    />
  );
};
